import { Injectable } from '@angular/core';
import { InspectionFactoryService } from '../../shared/services/inspectionFactory.service';
import { IndexedDbService } from '../../shared/services/indexedDb.service';
import { Observable, of } from 'rxjs';
import { flatMap, map } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { AppConstants } from '../../utils/appConstants.class';
import { DataService } from '../../shared/services';
import { InspectionPlugin, Asset } from '../../shared/models';

@Injectable()
export class FilesPluginFactoryService extends InspectionFactoryService {
  constructor(indexedDb: IndexedDbService, dataService: DataService) {
    super(indexedDb, dataService);
  }
  public addFiles(inspectionId: string, pointId: string, pluginId: string, files: any): Observable<any> {
    if (this.isManager) {
      return this.dataService.getPlugin(inspectionId, pluginId).pipe(
        map((plugin) => {
          if (plugin) {
            let imagesCounter = 0;
            let filesCounter = 0;
            const assetValues = [];
            const newAssets = [];
            files.forEach((file) => {
              const assetId = uuid();
              newAssets.push(assetId);
              plugin.config.Assets.push(assetId);

              if (typeof file === 'string') {
                assetValues.push({ assetId, key: `imageAssets[${imagesCounter}]` });
                imagesCounter++;
              } else {
                assetValues.push({ assetId, key: `fileAssets[${filesCounter}]` });
                filesCounter++;
              }
            });
            return { plugin, newAssets, assetValues };
          }
        })
      );
    }
    return this.getInspection(inspectionId).pipe(
      flatMap((inspection) => {
        const plugin = inspection.allPlugins.find((p) => p.id === pluginId);
        if (plugin) {
          let imagesCounter = 0;
          let filesCounter = 0;
          const assetValues = [];
          const obs = [];
          const newAssets = [];
          if (!inspection.assets) {
            inspection.assets = [];
          }
          files.forEach((file) => {
            const assetId = uuid();
            newAssets.push(assetId);
            plugin.config.Assets.push(assetId);
            if (typeof file === 'string') {
              if (this.isInspectionInOffline(inspectionId)) {
                obs.push(
                  Observable.fromPromise(fetch(file).then((res) => res.blob())).pipe(
                    map((blob) => {
                      const f = new File([blob], assetId, { type: 'image/png' });
                      inspection.assets.push(new Asset(assetId, assetId, 'image/png'));
                      return this.indexedDb.putImage(assetId, f, true);
                    })
                  )
                );
              }
              assetValues.push({ assetId, key: `imageAssets[${imagesCounter}]` });
              imagesCounter++;
            } else {
              if (this.isInspectionInOffline(inspectionId)) {
                inspection.assets.push(new Asset(assetId, file.name, file.type));
                obs.push(this.indexedDb.putImage(assetId, file, true));
              }
              assetValues.push({ assetId, key: `fileAssets[${filesCounter}]` });
              filesCounter++;
            }
          });
          plugin.etag = new Date();
          if (this.isInspectionInOffline(inspectionId)) {
            return Observable.zip(...obs).pipe(
              map((res) => {
                this.updateInspection(inspection);
                return { plugin, newAssets, assetValues };
              })
            );
          }
          this.updateInspection(inspection);
          return of({ plugin, newAssets, assetValues });
        }
        return of(null);
      })
    );
  }

  public editFile(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    assetId: string,
    file: any
  ): Observable<any> {
    if (this.isManager) {
      return this.dataService.getPlugin(inspectionId, pluginId).pipe(
        map((plugin) => {
          if (plugin) {
            let imagesCounter = 0;
            let filesCounter = 0;
            const assetValues = [];
            const newAssets = [];

            newAssets.push(assetId);

            if (!plugin.config.Assets.includes(assetId)) {
              plugin.config.Assets.push(assetId);
            }
            if (typeof file === 'string') {
              assetValues.push({ assetId, key: `imageAssets[${imagesCounter}]` });
              imagesCounter++;
            } else {
              assetValues.push({ assetId, key: `fileAssets[${filesCounter}]` });
              filesCounter++;
            }

            return { plugin, newAssets, assetValues };
          }
        })
      );
    }
    return this.getInspection(inspectionId).pipe(
      flatMap((inspection) => {
        const plugin = inspection.allPlugins.find((p) => p.id === pluginId);
        if (plugin) {
          let imagesCounter = 0;
          let filesCounter = 0;
          const assetValues = [];
          const obs = [];
          const newAssets = [];
          if (!inspection.assets) {
            inspection.assets = [];
          }

          newAssets.push(assetId);

          if (!plugin.config.Assets.includes(assetId)) {
            plugin.config.Assets.push(assetId);
          }
          if (typeof file === 'string') {
            if (this.isInspectionInOffline(inspectionId)) {
              obs.push(
                Observable.fromPromise(fetch(file).then((res) => res.blob())).pipe(
                  map((blob) => {
                    const f = new File([blob], assetId, { type: 'image/png' });
                    inspection.assets = inspection.assets.filter((a: Asset) => a.assetId !== assetId);
                    inspection.assets.push(new Asset(assetId, assetId, 'image/png'));
                    return this.indexedDb.putImage(assetId, f, true);
                  })
                )
              );
            }
            assetValues.push({ assetId, key: `imageAssets[${imagesCounter}]` });
            imagesCounter++;
          } else {
            if (this.isInspectionInOffline(inspectionId)) {
              inspection.assets = inspection.assets.filter((a: Asset) => a.assetId !== assetId);
              inspection.assets.push(new Asset(assetId, file.name, file.type));
              obs.push(this.indexedDb.putImage(assetId, file, true));
            }
            assetValues.push({ assetId, key: `fileAssets[${filesCounter}]` });
            filesCounter++;
          }
          plugin.etag = new Date();
          if (this.isInspectionInOffline(inspectionId)) {
            return Observable.zip(...obs).pipe(
              map((res) => {
                this.updateInspection(inspection);
                return { plugin, newAssets, assetValues };
              })
            );
          }

          this.updateInspection(inspection);
          return of({ plugin, newAssets, assetValues });
        }
        return of(null);
      })
    );
  }

  public deleteFile(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    assetId: string
  ): Observable<InspectionPlugin> {
    return this.deleteFiles(inspectionId, pointId, pluginId, [assetId]);
  }

  public deleteFiles(
    inspectionId: string,
    pointId: string,
    pluginId: string,
    assetIds: string[]
  ): Observable<InspectionPlugin> {
    if (this.isManager) {
      return this.dataService.getPlugin(inspectionId, pluginId).pipe(
        map((plugin) => {
          if (plugin) {
            plugin.config.Assets = plugin.config.Assets.filter((a) => !assetIds.includes(a));
            assetIds.forEach((a) => {
              if (plugin.config.Annotations && plugin.config.Annotations[a]) {
                delete plugin.config.Annotations[a];
              }
              if (plugin.config.AnnotationsComments && plugin.config.AnnotationsComments[a]) {
                delete plugin.config.AnnotationsComments[a];
              }
              if (plugin.config.Comments && plugin.config.Comments[a]) {
                delete plugin.config.Comments[a];
              }
              if (plugin.config.Svgs && plugin.config.Svgs[a]) {
                delete plugin.config.Svgs[a];
              }
              if (plugin.config.Types && plugin.config.Types[a]) {
                delete plugin.config.Types[a];
              }
            });
            return plugin;
          }
        })
      );
    }
    return this.getInspection(inspectionId).pipe(
      flatMap((inspection) => {
        const plugin = inspection.allPlugins.find((p) => p.id === pluginId);
        if (plugin) {
          const obs = [];
          plugin.config.Assets = plugin.config.Assets.filter((a) => !assetIds.includes(a));
          assetIds.forEach((a) => {
            if (plugin.config.Annotations && plugin.config.Annotations[a]) {
              delete plugin.config.Annotations[a];
            }
            if (plugin.config.AnnotationsComments && plugin.config.AnnotationsComments[a]) {
              delete plugin.config.AnnotationsComments[a];
            }
            if (plugin.config.Comments && plugin.config.Comments[a]) {
              delete plugin.config.Comments[a];
            }
            if (plugin.config.Svgs && plugin.config.Svgs[a]) {
              delete plugin.config.Svgs[a];
            }
            if (plugin.config.Types && plugin.config.Types[a]) {
              delete plugin.config.Types[a];
            }
          });
          if (this.isInspectionInOffline(inspectionId)) {
            inspection.assets = inspection.assets.filter((a) => !assetIds.includes(a.assetId));
            assetIds.forEach((assetId) => {
              obs.push(this.indexedDb.remove(AppConstants.INDEXEDDB_TABLES.images, assetId));
            });
          }
          plugin.etag = new Date();
          this.updateInspection(inspection);
          if (this.isInspectionInOffline(inspectionId)) {
            return Observable.zip(...obs).pipe(
              map((res) => {
                return plugin;
              })
            );
          }
          return of(plugin);
        }
        return of(null);
      })
    );
  }

  public editComment(inspectionId: string, pointId: string, pluginId: string, assetId: string, data: any) {
    if (this.isManager) {
      return this.dataService.getPlugin(inspectionId, pluginId).pipe(
        map((plugin) => {
          if (plugin) {
            if (!plugin.config.Comments) {
              plugin.config.Comments = {};
            }
            plugin.config.Comments[assetId] = data.comment;
            if (!plugin.config.Annotations) {
              plugin.config.Annotations = {};
            }
            plugin.config.Annotations[assetId] = data.annotation;

            if (!plugin.config.AnnotationsComments) {
              plugin.config.AnnotationsComments = {};
            }
            plugin.config.AnnotationsComments[assetId] = data.annotationComments;

            if (!plugin.config.Types) {
              plugin.config.Types = {};
            }

            plugin.config.Types[assetId] = data.type;

            if (!plugin.config.Svg) {
              plugin.config.Svg = {};
            }

            plugin.config.Svg[assetId] = data.svg;

            if (!plugin.config.Settings) {
              plugin.config.Settings = {};
            }

            plugin.config.Settings[assetId] = data.Settings;

            return plugin;
          }
        })
      );
    }
    return this.getInspection(inspectionId).pipe(
      map((inspection) => {
        const plugin = inspection.allPlugins.find((p) => p.id === pluginId);
        if (plugin) {
          if (!plugin.config.Comments) {
            plugin.config.Comments = {};
          }
          plugin.config.Comments[assetId] = data.comment;
          if (!plugin.config.Annotations) {
            plugin.config.Annotations = {};
          }
          plugin.config.Annotations[assetId] = data.annotation;
          if (!plugin.config.AnnotationsComments) {
            plugin.config.AnnotationsComments = {};
          }
          plugin.config.AnnotationsComments[assetId] = data.annotationComments;

          if (!plugin.config.Types) {
            plugin.config.Types = {};
          }

          plugin.config.Types[assetId] = data.type;

          if (!plugin.config.Svgs) {
            plugin.config.Svgs = {};
          }
          plugin.config.Svgs[assetId] = data.svg;
          plugin.etag = new Date();
          this.updateInspection(inspection);
          return plugin;
        }
        return of(null);
      })
    );
  }

  public getImageUrl(inspectionId: string, assetId: string): Observable<any> {
    return this.indexedDb.getImageUrl(assetId);
  }
}
